/**
 * Подарунок на день народження — попап із вибором (owner 2026-09-13:
 * «попап у нашому стилі, три вузькі картки, галочка — обрати подарунок,
 * і кнопка „забрати“»).
 *
 * Подарунок ОДИН на рік: три варіанти живуть в одному вікні й ділять відмітку
 * «забрано». Поки не забрав — банер висить на Головній (див.
 * `BirthdayGiftBanner`), тому подарунок неможливо не помітити.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { Icon } from "@/components/Icon";
import { TgEmoji } from "@/components/TgEmoji";
import { showAppAlert } from "@/lib/appAlert";
import { useCountdown } from "@/lib/countdown";
import { tg } from "@/lib/telegram";
import { useTopInset } from "@/lib/useTopInset";
import {
  birthdayGiftState,
  useBirthdayGift,
  useDeclineBirthdayGift,
  usePromoActivate,
  type BirthdayGiftOption,
  type BirthdayGiftOut,
  type BirthdayGiftState,
} from "@/api/hooks";

/** Текст під заголовком, коли подарунок недоступний (2026-09-26).
 *  Раніше будь-яка недоступність читалась як «ти вже забрав» — і людина, у
 *  якої просто минули 30 днів, чула, що нібито подарунок використала. */
const UNAVAILABLE_TEXT: Record<Exclude<BirthdayGiftState, "available">, string> = {
  redeemed: "Цьогорічний подарунок ти вже забрав. Наступний — рівно через рік.",
  expired:
    "Термін цьогорічного подарунка вже минув. Наступний подарунок чекатиме на тебе в день народження.",
  declined:
    "Від цьогорічного подарунка ти відмовився. Наступний подарунок чекатиме на тебе в день народження.",
  none: "Подарунок чекатиме на тебе в день народження.",
};

/** Рядок подарунка (owner 2026-09-13, дослівна правка):
 *  заголовок — В ОДИН РЯДОК, під ним опис, праворуч ціна. Де є стара ціна —
 *  вона стоїть НАД актуальною й перекреслена. */
function Row({
  option,
  checked,
  onPick,
}: {
  option: BirthdayGiftOption;
  checked: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      className={
        "flex w-full items-center gap-3 rounded-[0.875rem] border px-3 py-2.5 text-left " +
        (checked
          ? "border-primary/45 bg-primary/[0.06]"
          : "border-ink/[0.07] bg-bg/60")
      }
    >
      <span
        className={
          "grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 " +
          (checked ? "border-primary bg-primary" : "border-ink/20")
        }
      >
        {checked && <Icon name="check" size={11} color="#FFFFFF" />}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate font-display text-[0.8125rem] font-bold leading-tight text-ink">
          {option.title}
        </span>
        <span className="mt-0.5 flex items-center gap-1.5 text-[0.6875rem] leading-snug text-ink-dim">
          <span className="min-w-0 truncate">{option.subtitle}</span>
          {/* Таблетка: центруємо ФЛЕКСОМ, а не паддінгами. Unbounded має
              високу базову лінію, тому з `py-*` текст сідав нижче центру
              (owner 2026-09-13: «−55% і 1+1 не в центрі таблеток»). */}
          {option.pill && (
            <span className="inline-flex h-[1.125rem] shrink-0 items-center justify-center rounded-full bg-primary px-1.5 font-display text-[0.625rem] font-bold uppercase leading-none text-white">
              {/* Флекс центрує РЯДОК, а не ФАРБУ: у Unbounded капітальна висота
                  стоїть в em-боксі нижче центру, тому напис сідав на ~0.3px
                  нижче (на екрані DPR 3 — цілий піксель: замір на стенді давав
                  17/15). Компенсуємо константою шрифта в `em`, тому зсув
                  однаковий на будь-якому кеглі й будь-якому телефоні: після
                  правки замір 16/16 (DPR 3) і 53/51 (DPR 10).
                  Owner 2026-09-13: «−55% і 1+1 не в центрі таблеток». */}
              <span className="block -translate-y-[0.025em]">{option.pill}</span>
            </span>
          )}
        </span>
      </span>

      <span className="shrink-0 text-right">
        {option.old_amount && (
          <span className="block font-mono text-[0.6875rem] leading-none text-ink-faint line-through tabular-nums">
            {option.old_amount} ₴
          </span>
        )}
        <span className="block font-display text-[1.0625rem] font-bold leading-tight text-primary tabular-nums">
          {Number(option.amount)} ₴
        </span>
      </span>
    </button>
  );
}

export function BirthdayGiftModal({
  onClose,
  preview,
}: {
  onClose: () => void;
  /** Галерея попапів: мок-дані замість запиту, і жодних оплат чи відмов —
   *  кнопки лише закривають попап. */
  preview?: BirthdayGiftOut;
}) {
  const q = useBirthdayGift(!preview);
  const data = preview ?? q.data;
  const activate = usePromoActivate();
  const decline = useDeclineBirthdayGift();
  const topInset = useTopInset();
  const left = useCountdown(data?.expires_at ?? undefined).replace(
    /^через\s+/,
    "",
  );
  const [slug, setSlug] = useState<string | null>(null);

  // Перший варіант обраний за замовчуванням — щоб кнопка не була мертвою.
  useEffect(() => {
    const first = data?.options?.[0]?.slug;
    if (first && slug === null) setSlug(first);
  }, [data, slug]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const askDecline = () =>
    showAppAlert(
      "Якщо відмовишся, наступний подарунок буде лише через рік — цьогорічний зникне звідусіль.",
      {
        title: "Підтверди відмову",
        emoji: "🥲",
        buttons: [
          {
            label: "Так, відмовляюсь",
            variant: "danger",
            onClick: () =>
              preview
                ? onClose()
                : decline.mutate(undefined, { onSuccess: onClose }),
          },
          { label: "Скасувати", variant: "ghost" },
        ],
      },
    );

  const take = () => {
    if (!slug) return;
    tg()?.HapticFeedback?.impactOccurred?.("medium");
    if (preview) return onClose();
    activate.mutate(slug, {
      onSuccess: (res) => {
        const w = tg();
        if (res.purchase_url) {
          if (w?.openLink) w.openLink(res.purchase_url);
          else window.open(res.purchase_url, "_blank", "noopener,noreferrer");
        }
      },
      onError: (e) =>
        showAppAlert((e as Error).message, { title: "Не вдалося оформити" }),
    });
  };

  const options = data?.options ?? [];
  // Стан показуємо ТІЛЬКИ коли дані реально приїхали. Інакше на помилці
  // мережі попап брехав, що подарунок використаний.
  const state = data ? birthdayGiftState(data) : null;
  const gone = !!state && state !== "available";
  const loading = !preview && (q.isLoading || (!q.data && !q.isError));
  const failed = !preview && q.isError;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      style={{ paddingTop: topInset + 16 }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm overflow-hidden rounded-3xl bg-surface p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center">
          <TgEmoji char="🎂" size={40} />
          <div className="mt-2 font-display text-[1.0625rem] font-bold text-ink">
            З днем народження!
          </div>
          <div className="mt-1 text-[0.8125rem] leading-snug text-ink-dim [text-wrap:balance]">
            {loading
              ? "Дивлюсь, що для тебе приготували…"
              : failed
                ? "Не вдалося завантажити подарунок. Спробуй ще раз за хвилину."
                : gone
                  ? UNAVAILABLE_TEXT[state as Exclude<BirthdayGiftState, "available">]
                  : "У цей день ти можеш обрати 1 із 3 подарунків. Обери будь-який і натисни «Забрати подарунок»."}
          </div>
        </div>

        {!gone && !loading && !failed && (
          <>
            <div className="mt-4 space-y-2">
              {options.map((o) => (
                <Row
                  key={o.slug}
                  option={o}
                  checked={slug === o.slug}
                  onPick={() => {
                    tg()?.HapticFeedback?.selectionChanged?.();
                    setSlug(o.slug);
                  }}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={take}
              disabled={activate.isPending || !slug}
              className={
                "mt-4 block w-full rounded-full py-3.5 font-display text-[0.875rem] font-bold transition " +
                (activate.isPending || !slug
                  ? "bg-ink/10 text-ink-dim"
                  : "bg-primary text-white active:bg-primary-deep")
              }
            >
              {activate.isPending ? "Готую оплату…" : "Забрати подарунок"}
            </button>

            <div className="mt-2 text-center text-[0.6875rem] text-ink-faint">
              Подарунок згорає {left ? `через ${left.replace(/^через\s+/, "")}` : "за 30 днів"}
            </div>

            {/* Дві кнопки в один ряд (owner 2026-09-13): «Пізніше» лишає
                подарунок на місці, «Відмовитись» прибирає його на рік. */}
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-full bg-bg py-2.5 font-display text-[0.8125rem] font-semibold text-ink active:bg-ink/5"
              >
                Пізніше
              </button>
              <button
                type="button"
                onClick={askDecline}
                className="flex-1 rounded-full bg-bg py-2.5 font-display text-[0.8125rem] font-semibold text-ink-dim active:bg-ink/5"
              >
                Відмовитись
              </button>
            </div>
          </>
        )}

        {(gone || failed) && (
          <button
            type="button"
            onClick={onClose}
            className="mt-3 block w-full rounded-full bg-bg py-2.5 font-display text-[0.8125rem] font-semibold text-ink active:bg-ink/5"
          >
            Зрозуміло
          </button>
        )}

      </div>
    </div>,
    document.body,
  );
}

/** Банер на Головній — висить, поки подарунок не забрали (owner 2026-09-13). */
export function BirthdayGiftBanner({
  wrapperClassName,
  onOpen,
}: {
  wrapperClassName?: string;
  onOpen: () => void;
}) {
  const q = useBirthdayGift();
  const left = useCountdown(q.data?.expires_at ?? undefined).replace(
    /^через\s+/,
    "",
  );
  if (!q.data?.available) return null;

  const card = (
    <button
      type="button"
      onClick={() => {
        tg()?.HapticFeedback?.impactOccurred?.("medium");
        onOpen();
      }}
      className="relative w-full overflow-hidden rounded-ios-card border border-primary/25 bg-surface p-4 text-left shadow-card active:scale-[0.99]"
    >
      <div
        className="pointer-events-none absolute -right-8 -top-8 h-[6.875rem] w-[6.875rem] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(255,161,53,0.20), rgba(255,161,53,0))",
        }}
      />
      <div className="relative flex items-center gap-3">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-[0.875rem] bg-primary/10">
          <TgEmoji char="🎂" size={24} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-display text-[0.875rem] font-bold leading-tight text-ink">
              Подарунок на ДР
            </span>
            {left && (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 font-mono text-[0.625rem] text-primary">
                <Icon name="clock" size={10} color="#FE6B04" />
                {left}
              </span>
            )}
          </div>
          <div className="mt-1 text-[0.75rem] leading-snug text-ink-dim">
            Три варіанти на вибір — обери свій
          </div>
        </div>
        <Icon name="arrow-right" size={14} color="#FE6B04" />
      </div>
    </button>
  );

  return wrapperClassName ? <div className={wrapperClassName}>{card}</div> : card;
}
